<template>
  <section>
    <div class="panel">
      <div class="logs-head">
        <div>
          <h2>Logs ao vivo</h2>
          <p class="muted">
            Eventos da {{ assistantName }} (webhook, agente, tools, WhatsApp). Atualiza a cada poucos segundos.
          </p>
        </div>
        <div class="logs-actions">
          <label class="check compact">
            <input v-model="autoRefresh" type="checkbox" />
            Auto-atualizar
          </label>
          <button type="button" @click="load" :disabled="loading">Atualizar</button>
          <button type="button" class="ghost" @click="clearLogs">Limpar</button>
        </div>
      </div>

      <div class="filters">
        <label>
          Nível
          <select v-model="level">
            <option value="all">Todos</option>
            <option value="debug">debug+</option>
            <option value="info">info+</option>
            <option value="warn">warn+</option>
            <option value="error">error+</option>
          </select>
        </label>
        <label>
          Categoria
          <select v-model="category">
            <option value="all">Todas</option>
            <option v-for="c in categories" :key="c" :value="c">{{ c }}</option>
          </select>
        </label>
        <label class="grow">
          Busca
          <input v-model="query" type="search" placeholder="whatsapp, Izolda, tool…" />
        </label>
      </div>

      <p class="muted stats" v-if="stats">
        Buffer: {{ stats.total }} ·
        <span v-for="(n, k) in stats.byLevel" :key="k"> {{ k }}={{ n }} </span>
      </p>
    </div>

    <div class="panel logs-panel" style="margin-top: 1rem">
      <p class="muted" v-if="error">{{ error }}</p>
      <p class="muted" v-else-if="!entries.length">Nenhum log ainda. Converse com a {{ assistantName }} ou aguarde eventos.</p>
      <ul class="log-list" v-else>
        <li v-for="entry in entries" :key="entry.id" :class="['log-item', entry.level]">
          <div class="log-meta">
            <time>{{ formatTime(entry.time) }}</time>
            <span class="pill level">{{ entry.level }}</span>
            <span class="pill cat">{{ entry.category }}</span>
          </div>
          <p class="log-msg">{{ entry.message }}</p>
          <pre v-if="entry.data" class="log-data">{{ pretty(entry.data) }}</pre>
        </li>
      </ul>
    </div>
  </section>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted, ref, watch } from 'vue';
import { assistantName } from '../assistant-name';

type LogEntry = {
  id: string;
  time: string;
  level: string;
  category: string;
  message: string;
  data?: Record<string, unknown>;
};

const categories = [
  'system',
  'http',
  'webhook',
  'agent',
  'tools',
  'whatsapp',
  'auth',
  'browser',
  'reminder',
  'other',
];

const entries = ref<LogEntry[]>([]);
const stats = ref<{ total: number; byLevel: Record<string, number> } | null>(null);
const loading = ref(false);
const error = ref('');
const level = ref('info');
const category = ref('all');
const query = ref('');
const autoRefresh = ref(true);
let timer: ReturnType<typeof setInterval> | undefined;

function pretty(data: Record<string, unknown>) {
  try {
    return JSON.stringify(data, null, 2);
  } catch {
    return String(data);
  }
}

function formatTime(iso: string) {
  try {
    return new Date(iso).toLocaleString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      day: '2-digit',
      month: '2-digit',
    });
  } catch {
    return iso;
  }
}

async function load() {
  loading.value = true;
  error.value = '';
  try {
    const params = new URLSearchParams({
      limit: '200',
      level: level.value,
      category: category.value,
    });
    if (query.value.trim()) params.set('q', query.value.trim());
    const res = await fetch(`/api/logs?${params}`, { credentials: 'include' });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = (await res.json()) as {
      entries: LogEntry[];
      stats: { total: number; byLevel: Record<string, number> };
    };
    entries.value = data.entries ?? [];
    stats.value = data.stats ?? null;
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Falha ao carregar logs';
  } finally {
    loading.value = false;
  }
}

async function clearLogs() {
  if (!confirm('Limpar buffer de logs do portal?')) return;
  await fetch('/api/logs/clear', { method: 'POST', credentials: 'include' });
  await load();
}

function startTimer() {
  stopTimer();
  if (!autoRefresh.value) return;
  timer = setInterval(() => {
    void load();
  }, 3_000);
}

function stopTimer() {
  if (timer) clearInterval(timer);
  timer = undefined;
}

watch([level, category, query], () => {
  void load();
});
watch(autoRefresh, startTimer);

onMounted(() => {
  void load();
  startTimer();
});
onUnmounted(stopTimer);
</script>

<style scoped>
.logs-head {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  align-items: start;
}
.logs-actions {
  display: flex;
  gap: 0.6rem;
  align-items: center;
  flex-wrap: wrap;
}
.filters {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
  margin-top: 1rem;
}
.filters label {
  display: grid;
  gap: 0.3rem;
  font-size: 0.9rem;
}
.filters label.grow {
  flex: 1 1 180px;
}
.filters select,
.filters input,
.logs-actions button {
  font: inherit;
  padding: 0.45rem 0.65rem;
  border-radius: 0.55rem;
  border: 1px solid var(--line);
  background: #fff;
}
.logs-actions button.ghost {
  background: transparent;
}
.stats {
  margin-top: 0.75rem;
  font-size: 0.85rem;
}
.logs-panel {
  max-height: 70vh;
  overflow: auto;
}
.log-list {
  list-style: none;
  margin: 0;
  padding: 0;
}
.log-item {
  padding: 0.75rem 0;
  border-top: 1px solid var(--line);
}
.log-item:first-child {
  border-top: none;
}
.log-meta {
  display: flex;
  gap: 0.45rem;
  align-items: center;
  flex-wrap: wrap;
  margin-bottom: 0.25rem;
}
.log-meta time {
  font-variant-numeric: tabular-nums;
  color: var(--muted);
  font-size: 0.85rem;
}
.pill {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  padding: 0.12rem 0.4rem;
  border-radius: 999px;
  border: 1px solid var(--line);
}
.pill.level {
  font-weight: 700;
}
.log-item.info .pill.level {
  color: var(--ok);
  border-color: color-mix(in srgb, var(--ok) 40%, var(--line));
}
.log-item.warn .pill.level {
  color: var(--warn);
  border-color: color-mix(in srgb, var(--warn) 40%, var(--line));
}
.log-item.error .pill.level,
.log-item.fatal .pill.level {
  color: var(--down);
  border-color: color-mix(in srgb, var(--down) 40%, var(--line));
}
.log-msg {
  margin: 0;
  font-weight: 560;
}
.log-data {
  margin: 0.45rem 0 0;
  padding: 0.55rem 0.7rem;
  border-radius: 0.6rem;
  background: rgba(28, 42, 36, 0.05);
  font-size: 0.78rem;
  overflow: auto;
  max-height: 180px;
}
.check.compact {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.9rem;
}
</style>
